/*
 * Copyright (c) Nexign, JSC, 2023-2024.
 */
/**
 *
 *
 * @author Viacheslav Smalenski
 * @author Ilya Baranchikov
 * @version 1.30.0
 * @since 1.27.0
 */

import { ESubscriberIdActions, ISubscriberIdStatus } from '../../types';

export interface IInfoMeta {
    actions?: ESubscriberIdActions[];
    campaigns?: number;
    msisdn: string;
    status: ISubscriberIdStatus;
    statusPersonalData: ISubscriberIdStatus;
}

export interface IInfoTariffQuery {
    subscriberId: string;
    regionId?: number;
}

export interface IInfoTariff {
    actions?: ESubscriberIdActions[];
    // Остатки по пакетам трафика
    discounts?: IInfoTariffDiscount[];
    // Остатки по тарифному плану (рычаг changesCalculatingRemainsAndRatePlan)
    ratePlanRemains?: TInfoTariffRatePlanRemain[];
    // Остатки по тарифному плану (рычаг changesCalculatingRemainsAndRatePlan)
    othersRemains?: TInfoTariffRatePlanRemain[];
    // Остатки по тарифному плану (рычаг changesCalculatingRemainsAndRatePlan)
    piggyBankRemains?: TInfoTariffPiggyBankRemain[];
    // Тарифные опции
    options: Array<{
        date: string;
        name: string;
    }>;
    // Тарифный план
    ratePlan: {
        nextPlan?: {
            date: string;
            name: string;
        };
        description: string;
        name: string;
        payment?: Partial<{
            periodLength: string;
            error: boolean;
            unit: string;
            value: number;
        }>;
        isMegaPowersAvailable?: boolean;
        isMegaPowersButtonVisible?: boolean;
    };
    megaPowers?: IMegaPowerInfo[];
}

export interface IInfoTariffDiscount {
    isUnlimitedVolume: boolean;
    label: string;
    dateTo?: string;
    unit: string; // ед. измерения для текущего значения
    unitTotal: string; // ед. измерения для макс. кол-ва
    value: number; // текущее значение

    // тип добавлен в качестве временного решения, требуется реформат кода всего компонента
    valueWithUnits: number | string;
    valuePercentage: number; // отношение value к valueTotal c учетом конвертации значений (в %)
    valueTotal: number; // макс. кол-во

    isShared?: boolean;
    isSpend?: boolean;
    baseVolumeLider?: number;

    // тип string добавлен в качестве временного решения, требуется реформат кода всего компонента
    availableVolumeLider?: number | string;
    baseUnitsLider?: string;
    availableUnitsLider?: string;
}

interface IInfoTariffBaseRemain {
    name: string;
    dateTo: string;
}

export enum EUnitOfMeasure {
    BYTE = 'Байт',
    PIECE = 'Штука',
    SECOND = 'Секунда',
}

export interface IInfoTariffUnlimitedRatePlanRemain extends IInfoTariffBaseRemain {
    isUnlimitedVolume: true;
}
export interface IInfoTariffLimitedRatePlanRemain extends IInfoTariffBaseRemain {
    isUnlimitedVolume: false;
    baseVolume: number;
    availableVolume: number;
    unitOfMeasure?: string | EUnitOfMeasure;
    isShared?: boolean;
    isSpend?: boolean;
    baseVolumeLider?: number;
    availableVolumeLider?: number;
}

export type TInfoTariffRatePlanRemain = IInfoTariffUnlimitedRatePlanRemain | IInfoTariffLimitedRatePlanRemain;

export interface IInfoTarrifUnlimitedPiggyBankRemain extends IInfoTariffBaseRemain {
    isUnlimitedVolume: true;
}

export interface IInfoTarrifLimitedPiggyBankRemain extends IInfoTariffBaseRemain {
    isUnlimitedVolume: false;
    baseVolume: number;
    availableVolume: number;
    unitOfMeasure?: string | EUnitOfMeasure;
    isShared?: boolean;
    isSpend?: boolean;
    baseVolumeLider?: number;
    availableVolumeLider?: number;
}

export type TInfoTariffPiggyBankRemain = IInfoTarrifUnlimitedPiggyBankRemain | IInfoTarrifLimitedPiggyBankRemain;

export interface IInfoPiggyBank {
    unit: string;
    value: number;
    valueTotal: number;
}

export interface IMegaPower {
    id: number;
    name: string;
    description: string;
    connected: boolean;
    readOnly: boolean;
    recurringCharge: {
        priceCanBeChanged: boolean;
        periodLength: number;
        periodUnitName: string;
        price: number;
        seeDetails: boolean;
    } | null;
    oneTimeCharge?: {
        priceCanBeChanged: boolean;
        price: number;
    };
}

export type IMegaPowerInfo = Pick<IMegaPower, 'name' | 'description'>;
